import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';

const PrivacyPolicy = () => {
  const { language } = useLanguage();
  const lastUpdated = { en: "February 11, 2026", fi: "11. helmikuuta 2026" };

  const content = {
    en: {
      backHome: "Back to Home",
      title: "Privacy Policy",
      lastUpdatedLabel: "Last updated",
      controllerHeading: "2. Data Controller",
      controllerFields: { company: "Company", businessId: "Business ID", location: "Location", email: "Email", dpoLabel: "Data Protection Contact" },
      supervisoryHeading: "12. Supervisory Authority",
      supervisoryBody: {
        name: "Finnish Data Protection Ombudsman",
        address: "Lintulahdenkuja 4, 00530 Helsinki, Finland",
        phoneLabel: "Phone",
        websiteLabel: "Website"
      },
      contactHeading: "13. Contact Us",
      contactIntro: "For any privacy-related questions or to exercise your rights:",
      contactFields: { email: "Email", general: "General Inquiries", address: "Address" },
      sections: [
        {
          title: "1. Introduction",
          paragraphs: [
            "2DS Finland Oy (\"we\", \"us\", \"our\"), a company registered in Finland (Business ID: 3246359-5), is committed to protecting your privacy and personal data in accordance with the General Data Protection Regulation (GDPR) (EU) 2016/679 and Finnish data protection legislation.",
            "This Privacy Policy explains how we collect, use, store, and protect your personal data when you visit our website (2ds.fi) or use our services."
          ]
        },
        {
          title: "3. Personal Data We Collect",
          paragraphs: ["We may collect the following categories of personal data:"],
          subsections: [
            { title: "3.1 Data You Provide Directly", items: ["Contact information (name, email address, phone number)", "Company information (company name, business ID, address)", "Communication content (messages, inquiries, feedback)"] },
            { title: "3.2 Data Collected Automatically", items: ["IP address and approximate location", "Browser type and version", "Device information (type, operating system)", "Pages visited and time spent on our website", "Referral source", "Cookie data (see our Cookie Policy for details)"] }
          ]
        },
        {
          title: "4. Legal Basis for Processing",
          paragraphs: ["Under GDPR Article 6, we process your personal data based on:"],
          bullets: [
            "<strong>Consent (Art. 6(1)(a)):</strong> For analytics cookies and marketing communications",
            "<strong>Contract (Art. 6(1)(b)):</strong> To provide services you have requested",
            "<strong>Legitimate Interest (Art. 6(1)(f)):</strong> For website security, fraud prevention, and business development",
            "<strong>Legal Obligation (Art. 6(1)(c)):</strong> To comply with Finnish and EU laws"
          ]
        },
        {
          title: "5. How We Use Your Data",
          bullets: [
            "To respond to your inquiries and provide customer support",
            "To provide and improve our services",
            "To send relevant business communications (with your consent)",
            "To analyze website usage and improve user experience",
            "To ensure website security and prevent fraud",
            "To comply with legal obligations"
          ]
        },
        {
          title: "6. Data Sharing and Transfers",
          paragraphs: ["We may share your data with:"],
          bullets: [
            "<strong>Service Providers:</strong> Third-party services that help us operate (hosting, analytics)",
            "<strong>Legal Requirements:</strong> When required by law or to protect our rights",
            "<strong>Business Transfers:</strong> In case of merger, acquisition, or sale of assets"
          ],
          paragraphsAfter: ["<strong>International Transfers:</strong> If we transfer data outside the EU/EEA, we ensure appropriate safeguards are in place (Standard Contractual Clauses, adequacy decisions, or your explicit consent)."]
        },
        {
          title: "7. Your Rights Under GDPR",
          paragraphs: ["You have the following rights regarding your personal data:"],
          bullets: [
            "<strong>Right of Access (Art. 15):</strong> Request a copy of your personal data",
            "<strong>Right to Rectification (Art. 16):</strong> Correct inaccurate or incomplete data",
            "<strong>Right to Erasure (Art. 17):</strong> Request deletion of your data (\"right to be forgotten\")",
            "<strong>Right to Restriction (Art. 18):</strong> Limit how we process your data",
            "<strong>Right to Data Portability (Art. 20):</strong> Receive your data in a portable format",
            "<strong>Right to Object (Art. 21):</strong> Object to processing based on legitimate interest",
            "<strong>Right to Withdraw Consent (Art. 7):</strong> Withdraw consent at any time",
            "<strong>Right to Lodge a Complaint:</strong> File a complaint with the Finnish Data Protection Ombudsman"
          ],
          paragraphsAfter: ["To exercise these rights, contact us at <a href=\"mailto:privacy@2ds.fi\" class=\"text-accent-teal underline\">privacy@2ds.fi</a>. We will respond within 30 days as required by GDPR."]
        },
        {
          title: "8. Data Retention",
          paragraphs: ["We retain personal data only as long as necessary for the purposes described in this policy:"],
          bullets: [
            "Contact inquiries: 3 years from last contact",
            "Business relationships: Duration of relationship + 7 years (legal requirement)",
            "Analytics data: 26 months",
            "Cookie consent records: 12 months"
          ]
        },
        {
          title: "9. Data Security",
          paragraphs: ["We implement appropriate technical and organizational measures to protect your data:"],
          bullets: [
            "SSL/TLS encryption for data in transit",
            "Secure data storage with access controls",
            "Regular security assessments",
            "Employee training on data protection"
          ]
        },
        {
          title: "10. Children's Privacy",
          paragraphs: ["Our services are not directed to individuals under 16 years of age. We do not knowingly collect personal data from children. If you believe we have collected data from a child, please contact us."]
        },
        {
          title: "11. Changes to This Policy",
          paragraphs: ["We may update this Privacy Policy periodically. We will notify you of significant changes by posting a notice on our website. The \"Last updated\" date indicates when changes were made."]
        }
      ]
    },
    fi: {
      backHome: "Takaisin etusivulle",
      title: "Tietosuojaseloste",
      lastUpdatedLabel: "Päivitetty",
      controllerHeading: "2. Rekisterinpitäjä",
      controllerFields: { company: "Yritys", businessId: "Y-tunnus", location: "Sijainti", email: "Sähköposti", dpoLabel: "Tietosuojayhteyshenkilö" },
      supervisoryHeading: "12. Valvontaviranomainen",
      supervisoryBody: {
        name: "Tietosuojavaltuutetun toimisto",
        address: "Lintulahdenkuja 4, 00530 Helsinki, Suomi",
        phoneLabel: "Puhelin",
        websiteLabel: "Verkkosivusto"
      },
      contactHeading: "13. Yhteystiedot",
      contactIntro: "Tietosuojaan liittyvissä kysymyksissä tai oikeuksiesi käyttämiseksi:",
      contactFields: { email: "Sähköposti", general: "Yleiset tiedustelut", address: "Osoite" },
      sections: [
        {
          title: "1. Johdanto",
          paragraphs: [
            "2DS Finland Oy (\"me\", \"meidän\"), Suomeen rekisteröity yhtiö (Y-tunnus: 3246359-5), on sitoutunut suojaamaan yksityisyyttäsi ja henkilötietojasi EU:n yleisen tietosuoja-asetuksen (GDPR) 2016/679 sekä Suomen tietosuojalainsäädännön mukaisesti.",
            "Tässä tietosuojaselosteessa kuvataan, miten keräämme, käytämme, säilytämme ja suojaamme henkilötietojasi käyttäessäsi verkkosivustoamme (2ds.fi) tai palveluitamme."
          ]
        },
        {
          title: "3. Käsittelemämme henkilötiedot",
          paragraphs: ["Voimme kerätä seuraavia henkilötietoryhmiä:"],
          subsections: [
            { title: "3.1 Suoraan antamasi tiedot", items: ["Yhteystiedot (nimi, sähköpostiosoite, puhelinnumero)", "Yritystiedot (yrityksen nimi, Y-tunnus, osoite)", "Viestintäsisältö (viestit, tiedustelut, palaute)"] },
            { title: "3.2 Automaattisesti kerätyt tiedot", items: ["IP-osoite ja arvioitu sijainti", "Selaintyyppi ja -versio", "Laitetiedot (tyyppi, käyttöjärjestelmä)", "Vierailut sivuilla ja niillä vietetty aika", "Viittauslähde", "Evästetiedot (katso evästekäytäntömme)"] }
          ]
        },
        {
          title: "4. Käsittelyn oikeusperuste",
          paragraphs: ["GDPR-asetuksen 6 artiklan mukaisesti käsittelemme henkilötietojasi seuraavin perustein:"],
          bullets: [
            "<strong>Suostumus (6(1)(a) art.):</strong> Analytiikkaevästeet ja markkinointiviestintä",
            "<strong>Sopimus (6(1)(b) art.):</strong> Pyytämiesi palveluiden tarjoaminen",
            "<strong>Oikeutettu etu (6(1)(f) art.):</strong> Sivuston tietoturva, väärinkäytösten ehkäisy ja liiketoiminnan kehittäminen",
            "<strong>Lakisääteinen velvoite (6(1)(c) art.):</strong> Suomen ja EU:n lainsäädännön noudattaminen"
          ]
        },
        {
          title: "5. Tietojen käyttötarkoitukset",
          bullets: [
            "Tiedusteluihisi vastaaminen ja asiakaspalvelu",
            "Palveluidemme tarjoaminen ja kehittäminen",
            "Olennaisen liikeviestinnän lähettäminen (suostumuksellasi)",
            "Sivuston käytön analysointi ja käyttökokemuksen parantaminen",
            "Sivuston tietoturvan varmistaminen ja petosten ehkäisy",
            "Lakisääteisten velvoitteiden noudattaminen"
          ]
        },
        {
          title: "6. Tietojen luovutus ja siirrot",
          paragraphs: ["Voimme luovuttaa tietojasi seuraaville tahoille:"],
          bullets: [
            "<strong>Palveluntarjoajat:</strong> Toimintaamme tukevat ulkopuoliset palvelut (isännöinti, analytiikka)",
            "<strong>Lakisääteiset vaatimukset:</strong> Kun laki sitä vaatii tai oikeuksiemme suojaamiseksi",
            "<strong>Liiketoimintajärjestelyt:</strong> Sulautumisen, yritysoston tai liiketoiminnan luovutuksen yhteydessä"
          ],
          paragraphsAfter: ["<strong>Kansainväliset siirrot:</strong> Jos siirrämme tietoja EU/ETA-alueen ulkopuolelle, varmistamme riittävät suojatoimet (vakiosopimuslausekkeet, riittävyyspäätökset tai nimenomainen suostumuksesi)."]
        },
        {
          title: "7. Oikeutesi GDPR:n mukaan",
          paragraphs: ["Sinulla on seuraavat oikeudet henkilötietojesi osalta:"],
          bullets: [
            "<strong>Oikeus saada pääsy tietoihin (15 art.):</strong> Pyytää kopio henkilötiedoistasi",
            "<strong>Oikeus tietojen oikaisemiseen (16 art.):</strong> Korjata virheelliset tai puutteelliset tiedot",
            "<strong>Oikeus tietojen poistamiseen (17 art.):</strong> Pyytää tietojesi poistamista (\"oikeus tulla unohdetuksi\")",
            "<strong>Oikeus käsittelyn rajoittamiseen (18 art.):</strong> Rajoittaa tietojesi käsittelyä",
            "<strong>Oikeus siirtää tiedot järjestelmästä toiseen (20 art.):</strong> Saada tietosi siirrettävässä muodossa",
            "<strong>Vastustamisoikeus (21 art.):</strong> Vastustaa oikeutettuun etuun perustuvaa käsittelyä",
            "<strong>Oikeus peruuttaa suostumus (7 art.):</strong> Peruuttaa suostumus milloin tahansa",
            "<strong>Oikeus tehdä valitus:</strong> Tehdä valitus tietosuojavaltuutetulle"
          ],
          paragraphsAfter: ["Oikeuksien käyttämiseksi ota meihin yhteyttä osoitteessa <a href=\"mailto:privacy@2ds.fi\" class=\"text-accent-teal underline\">privacy@2ds.fi</a>. Vastaamme 30 päivän kuluessa GDPR:n edellyttämällä tavalla."]
        },
        {
          title: "8. Tietojen säilytysaika",
          paragraphs: ["Säilytämme henkilötietoja vain niin kauan kuin on tarpeen tässä selosteessa kuvattuihin tarkoituksiin:"],
          bullets: [
            "Yhteydenotot: 3 vuotta viimeisestä yhteydenotosta",
            "Liikesuhteet: Suhteen kesto + 7 vuotta (lakisääteinen vaatimus)",
            "Analytiikkatiedot: 26 kuukautta",
            "Evästesuostumustiedot: 12 kuukautta"
          ]
        },
        {
          title: "9. Tietoturva",
          paragraphs: ["Toteutamme asianmukaisia teknisiä ja organisatorisia toimenpiteitä tietojesi suojaamiseksi:"],
          bullets: [
            "SSL/TLS-salaus tiedonsiirrossa",
            "Suojattu tietojen säilytys pääsynhallinnalla",
            "Säännölliset tietoturva-arvioinnit",
            "Henkilöstön tietosuojakoulutus"
          ]
        },
        {
          title: "10. Lasten yksityisyys",
          paragraphs: ["Palvelumme eivät ole suunnattu alle 16-vuotiaille. Emme tietoisesti kerää henkilötietoja lapsilta. Jos epäilet, että olemme keränneet tietoja lapselta, otathan yhteyttä."]
        },
        {
          title: "11. Selosteen muutokset",
          paragraphs: ["Saatamme päivittää tätä tietosuojaselostetta ajoittain. Ilmoitamme merkittävistä muutoksista verkkosivuillamme. Sivun yläosassa oleva \"Päivitetty\"-päiväys osoittaa viimeisimmän tarkistuksen."]
        }
      ]
    }
  };

  const c = content[language] || content.en;
  const lu = lastUpdated[language] || lastUpdated.en;

  return (
    <div className="min-h-screen bg-parchment" data-testid="privacy-policy-page">
      {/* Header */}
      <header className="border-b border-warm py-6">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center gap-2 text-ink-muted hover:text-ink transition-colors" data-testid="privacy-policy-back-home">
            <ArrowLeft className="w-4 h-4" />
            {c.backHome}
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="font-serif text-4xl md:text-5xl text-ink mb-4">{c.title}</h1>
        <p className="text-ink-muted mb-8">{c.lastUpdatedLabel}: {lu}</p>

        <div className="prose prose-lg max-w-none">
          {/* Section 1 */}
          {c.sections[0] && (
            <section className="mb-12">
              <h2 className="font-serif text-2xl text-ink mb-4">{c.sections[0].title}</h2>
              {c.sections[0].paragraphs.map((p, i) => (
                <p key={i} className="text-ink-light mb-4">{p}</p>
              ))}
            </section>
          )}

          {/* Section 2 — Controller card */}
          <section className="mb-12">
            <h2 className="font-serif text-2xl text-ink mb-4">{c.controllerHeading}</h2>
            <div className="bg-cream/50 p-6 rounded-lg">
              <p className="text-ink-light mb-2"><strong>{c.controllerFields.company}:</strong> 2DS Finland Oy</p>
              <p className="text-ink-light mb-2"><strong>{c.controllerFields.businessId}:</strong> 3246359-5</p>
              <p className="text-ink-light mb-2"><strong>{c.controllerFields.location}:</strong> Espoo, Finland</p>
              <p className="text-ink-light mb-2"><strong>{c.controllerFields.email}:</strong> sales@2ds.fi</p>
              <p className="text-ink-light"><strong>{c.controllerFields.dpoLabel}:</strong> privacy@2ds.fi</p>
            </div>
          </section>

          {/* Sections 3-11 from data */}
          {c.sections.slice(1).map((section, sIdx) => (
            <section key={sIdx} className="mb-12">
              <h2 className="font-serif text-2xl text-ink mb-4">{section.title}</h2>
              {section.paragraphs?.map((p, i) => (
                <p key={`p-${i}`} className="text-ink-light mb-4">{p}</p>
              ))}
              {section.subsections?.map((sub, subIdx) => (
                <div key={`sub-${subIdx}`}>
                  <h3 className="font-semibold text-ink mb-2">{sub.title}</h3>
                  <ul className="list-disc pl-6 text-ink-light mb-4">
                    {sub.items.map((it, j) => <li key={j}>{it}</li>)}
                  </ul>
                </div>
              ))}
              {section.bullets && (
                <ul className="list-disc pl-6 text-ink-light mb-4">
                  {section.bullets.map((b, i) => (
                    <li key={i} dangerouslySetInnerHTML={{ __html: b }} />
                  ))}
                </ul>
              )}
              {section.paragraphsAfter?.map((p, i) => (
                <p key={`pa-${i}`} className="text-ink-light mb-4" dangerouslySetInnerHTML={{ __html: p }} />
              ))}
            </section>
          ))}

          {/* Section 12 — Supervisory authority card */}
          <section className="mb-12">
            <h2 className="font-serif text-2xl text-ink mb-4">{c.supervisoryHeading}</h2>
            <div className="bg-cream/50 p-6 rounded-lg">
              <p className="text-ink-light mb-2"><strong>{c.supervisoryBody.name}</strong></p>
              <p className="text-ink-light mb-2">{c.supervisoryBody.address}</p>
              <p className="text-ink-light mb-2">{c.supervisoryBody.phoneLabel}: +358 29 566 6700</p>
              <p className="text-ink-light">{c.supervisoryBody.websiteLabel}: <a href="https://tietosuoja.fi" className="text-accent-teal underline" target="_blank" rel="noopener noreferrer">tietosuoja.fi</a></p>
            </div>
          </section>

          {/* Section 13 — Contact card */}
          <section className="mb-12">
            <h2 className="font-serif text-2xl text-ink mb-4">{c.contactHeading}</h2>
            <p className="text-ink-light mb-4">{c.contactIntro}</p>
            <div className="bg-cream/50 p-6 rounded-lg">
              <p className="text-ink-light mb-2"><strong>{c.contactFields.email}:</strong> <a href="mailto:privacy@2ds.fi" className="text-accent-teal underline">privacy@2ds.fi</a></p>
              <p className="text-ink-light mb-2"><strong>{c.contactFields.general}:</strong> <a href="mailto:sales@2ds.fi" className="text-accent-teal underline">sales@2ds.fi</a></p>
              <p className="text-ink-light"><strong>{c.contactFields.address}:</strong> 2DS Finland Oy, Espoo, Finland</p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-warm py-8 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-ink-muted text-sm">
            © {new Date().getFullYear()} 2DS Finland Oy. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
